import { useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Copy, X } from 'lucide-react'
import {
  Button,
  Checkbox,
  Chip,
  Drawer,
  Input,
  PartnerPicker,
  ProductPicker,
  Textarea,
  type PickerValue,
} from '@/components/ui'
import {
  suggestedTypes,
  useCreateDocRequest,
  useProductCoverage,
  useRequestableTypes,
  useVendorProducts,
  type LinkResponse,
  type ProductCoverage,
} from '../api/doc-requests'

interface Selected {
  product_tmpl_id: number
  name: string
  default_code: string | null
  doc_types: string[]
}

export interface RequestDocsDrawerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onOpenCard?: (cardId: string) => void
  /** Prefill from a product page / expiring row / an existing card's products. */
  initialProducts?: { product_tmpl_id: number; name: string; default_code?: string | null; doc_types?: string[] }[]
  /** Link the new vendor link to an existing card (e.g. one triaged from email). */
  regulatoryRequestId?: string
  initialVendor?: PickerValue
}

export function RequestDocsDrawer(props: RequestDocsDrawerProps) {
  // Remount per open so a second request starts clean.
  return props.open ? <RequestDocsDrawerBody {...props} /> : null
}

function RequestDocsDrawerBody({
  open,
  onOpenChange,
  onOpenCard,
  initialProducts,
  regulatoryRequestId,
  initialVendor = null,
}: RequestDocsDrawerProps) {
  const { data: types = [] } = useRequestableTypes()
  const requestable = useMemo(() => new Set(types.map((t) => t.key)), [types])
  const labels = useMemo(() => Object.fromEntries(types.map((t) => [t.key, t.label])), [types])
  const [vendor, setVendor] = useState<PickerValue>(initialVendor)
  const vendorProducts = useVendorProducts(vendor ? vendor[0] : null)
  const [selected, setSelected] = useState<Selected[]>(
    (initialProducts ?? []).map((p) => ({ ...p, default_code: p.default_code ?? null, doc_types: p.doc_types ?? [] })),
  )
  const coverage = useProductCoverage(selected.map((s) => s.product_tmpl_id))
  const coverageById = useMemo(() => {
    const m = new Map<number, ProductCoverage>()
    for (const c of vendorProducts.data?.items ?? []) m.set(c.product_tmpl_id, c)
    for (const c of coverage.data?.items ?? []) m.set(c.product_tmpl_id, c)
    return m
  }, [vendorProducts.data, coverage.data])
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [message, setMessage] = useState('')
  const [days, setDays] = useState(14)
  const [result, setResult] = useState<LinkResponse | null>(null)
  const create = useCreateDocRequest()

  const add = (p: { product_tmpl_id: number; name: string; default_code: string | null }) => {
    if (selected.some((s) => s.product_tmpl_id === p.product_tmpl_id)) return
    setSelected((cur) => [...cur, { ...p, doc_types: suggestedTypes(coverageById.get(p.product_tmpl_id), requestable) }])
  }
  const remove = (pid: number) => setSelected((cur) => cur.filter((s) => s.product_tmpl_id !== pid))
  const toggleType = (pid: number, key: string, on: boolean) =>
    setSelected((cur) => cur.map((s) => s.product_tmpl_id !== pid ? s
      : { ...s, doc_types: on ? [...s.doc_types, key] : s.doc_types.filter((k) => k !== key) }))

  const incomplete = selected.filter((s) => !s.doc_types.length)
  const canCreate = !!vendor && selected.length > 0 && !incomplete.length && !create.isPending

  const submit = () => {
    if (!vendor) return
    create.mutate(
      {
        vendor_partner_id: vendor[0],
        products: selected.map((s) => ({ product_tmpl_id: s.product_tmpl_id, doc_types: s.doc_types })),
        contact_name: contactName.trim() || null,
        contact_email: contactEmail.trim() || null,
        message_to_vendor: message.trim() || null,
        days,
        regulatory_request_id: regulatoryRequestId ?? null,
      },
      {
        onSuccess: setResult,
        onError: (err) => toast.error('Could not create the request', { description: (err as Error).message }),
      },
    )
  }

  return (
    <Drawer
      surface="form"
      size="wide"
      open={open}
      onOpenChange={onOpenChange}
      title={result ? `${result.request.reference} created` : 'Request documents from a vendor'}
      meta={result
        ? 'Copy the link and send it to the vendor from your mailbox'
        : 'The vendor uploads through a secure link. Regulatory and operations are emailed when they submit.'}
      footer={
        <div className="flex justify-end gap-2">
          {result ? (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Close</Button>
              {result.card_id && onOpenCard ? (
                <Button variant="primary" onClick={() => { onOpenChange(false); onOpenCard(result.card_id!) }}>Open card</Button>
              ) : null}
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
              <Button variant="primary" disabled={!canCreate} loading={create.isPending} onClick={submit}>Create link</Button>
            </>
          )}
        </div>
      }
    >
      {result ? <LinkResult result={result} /> : (
        <div className="flex flex-col gap-5 p-4">
          <label className="flex flex-col gap-1">
            <span className="text-sm text-secondary">Vendor</span>
            <PartnerPicker kind="vendor" variant="field" value={vendor} onChange={setVendor} />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1">
              <span className="text-sm text-secondary">Vendor contact name</span>
              <Input value={contactName} maxLength={255} onChange={(e) => setContactName(e.target.value)} />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-sm text-secondary">Contact email (not emailed by the system)</span>
              <Input type="email" value={contactEmail} maxLength={255} onChange={(e) => setContactEmail(e.target.value)} />
            </label>
          </div>

          {vendor ? (
            <VendorProductList
              loading={vendorProducts.isLoading}
              items={vendorProducts.data?.items ?? []}
              selectedIds={new Set(selected.map((s) => s.product_tmpl_id))}
              labels={labels}
              onAdd={add}
            />
          ) : null}

          <div className="flex flex-col gap-1">
            <span className="text-sm text-secondary">Add any product</span>
            <ProductPicker
              value={null}
              searchEndpoint="/api/product-documents/search/products"
              placeholder="Search products by name or code…"
              onChange={(v) => v && add({ product_tmpl_id: v[0], name: v[1], default_code: null })}
            />
          </div>

          {selected.length ? (
            <div className="flex flex-col gap-3">
              <span className="text-sm font-medium text-primary">Documents to request</span>
              {selected.map((s) => (
                <SelectedProduct key={s.product_tmpl_id} item={s} coverage={coverageById.get(s.product_tmpl_id)}
                  types={types} onToggle={toggleType} onRemove={remove} />
              ))}
            </div>
          ) : (
            <p className="text-sm text-secondary">Choose a vendor to see the products they supply, or search for any product.</p>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-sm text-secondary">Note shown to the vendor (optional)</span>
            <Textarea rows={2} value={message} maxLength={2000} onChange={(e) => setMessage(e.target.value)} />
          </label>
          <label className="flex items-center gap-2 text-sm text-secondary">
            Link works for
            <Input type="number" min={1} max={14} value={days} className="w-20"
              onChange={(e) => setDays(Math.max(1, Math.min(14, Number(e.target.value) || 14)))} />
            days
          </label>
        </div>
      )}
    </Drawer>
  )
}

function VendorProductList({
  loading,
  items,
  selectedIds,
  labels,
  onAdd,
}: {
  loading: boolean
  items: ProductCoverage[]
  selectedIds: Set<number>
  labels: Record<string, string>
  onAdd: (p: { product_tmpl_id: number; name: string; default_code: string | null }) => void
}) {
  if (loading) return <p className="text-sm text-secondary">Loading the vendor's products…</p>
  if (!items.length) return <p className="text-sm text-secondary">No products list this vendor. Search for a product below.</p>
  return (
    <div className="flex flex-col gap-1">
      <span className="text-sm text-secondary">Products from this vendor ({items.length})</span>
      <div className="flex max-h-64 flex-col overflow-auto rounded-md border border-border-subtle">
        {items.map((p) => {
          const gaps = [...p.missing_required.map((k) => `Missing ${labels[k] ?? k}`),
            ...p.expiring.map((k) => `${labels[k] ?? k} expiring`)]
          const chosen = selectedIds.has(p.product_tmpl_id)
          return (
            <div key={p.product_tmpl_id} className="flex items-center gap-3 border-b border-border-subtle px-3 py-2 last:border-b-0">
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm text-primary">{p.name}{p.default_code ? ` · ${p.default_code}` : ''}</div>
                <div className="truncate text-xs text-secondary">
                  {gaps.length ? gaps.join(' · ') : `${Object.keys(p.current).length} documents on file`}
                </div>
              </div>
              <Button size="sm" variant={chosen ? 'ghost' : 'secondary'} disabled={chosen} onClick={() => onAdd(p)}>
                {chosen ? 'Added' : 'Add'}
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SelectedProduct({
  item,
  coverage,
  types,
  onToggle,
  onRemove,
}: {
  item: Selected
  coverage: ProductCoverage | undefined
  types: { key: string; label: string }[]
  onToggle: (pid: number, key: string, on: boolean) => void
  onRemove: (pid: number) => void
}) {
  const code = item.default_code ?? coverage?.default_code
  return (
    <div className="rounded-md border border-border-default p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex-1 text-sm font-medium text-primary">{item.name}{code ? ` · ${code}` : ''}</span>
        {!item.doc_types.length ? <Chip size="xs" variant="warning">Choose at least one</Chip> : null}
        <Button size="sm" variant="ghost" aria-label={`Remove ${item.name}`} onClick={() => onRemove(item.product_tmpl_id)}>
          <X size={14} />
        </Button>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        {types.map((t) => {
          const have = coverage?.current[t.key]
          const hint = coverage?.missing_required.includes(t.key) ? 'missing'
            : coverage?.expiring.includes(t.key) ? `expires ${have?.expires_on ?? ''}`
              : have ? 'on file' : null
          return (
            <Checkbox key={t.key} checked={item.doc_types.includes(t.key)}
              label={hint ? `${t.label} (${hint})` : t.label}
              onChange={(e) => onToggle(item.product_tmpl_id, t.key, e.target.checked)} />
          )
        })}
      </div>
    </div>
  )
}

function LinkResult({ result }: { result: LinkResponse }) {
  const copy = async (text: string, what: string) => {
    try {
      await navigator.clipboard.writeText(text)
      toast.success(`${what} copied`)
    } catch {
      toast.warning('Copy failed; select the text and copy it')
    }
  }
  return (
    <div className="flex flex-col gap-4 p-4">
      <p className="text-sm text-secondary">
        This link is shown only now. Anyone with it can upload to this request until it expires, so send it only to the
        vendor's contact. If it's lost, create a new link from the card.
      </p>
      <div className="flex items-center gap-2">
        <Input readOnly value={result.link} aria-label="Vendor link" onFocus={(e) => e.currentTarget.select()} />
        <Button variant="secondary" onClick={() => void copy(result.link, 'Link')}><Copy size={14} /> Copy</Button>
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <span className="text-sm text-secondary">Suggested email · Subject: {result.suggested_subject}</span>
          <Button size="sm" variant="secondary" onClick={() => void copy(result.suggested_email, 'Email')}>
            <Copy size={14} /> Copy email
          </Button>
        </div>
        <Textarea readOnly rows={9} value={result.suggested_email} aria-label="Suggested email" />
      </div>
    </div>
  )
}
